<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { Badge, Button, Card, Select } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { approveCreatorProject, createCreatorProjectPreview, getCreatorProject, getCreatorProjectInputAttachment, rejectCreatorProject, retryCreatorProject, startCreatorProject } from '../api/creator-projects.js'
import { visualGamePreviewUrl } from '../lib/visual-game-preview-url.js'

const props = defineProps({ id: { type: String, required: true } })
const { t } = useI18n()
const detail = ref(null)
const reason = ref('')
const busy = ref(false)
const attachmentBusy = ref(false)
const attachmentError = ref('')
const productionError = ref('')
const attachmentUrl = ref('')
const attachmentText = ref('')
const previewLocale = ref('en')
const pending = computed(() => detail.value?.project.status === 'pending_review')
const attachments = computed(() => detail.value === null ? [] : detail.value.task.attachments)
const activeAttachmentIndex = ref(null)
const attachment = computed(() => activeAttachmentIndex.value === null ? null : attachments.value[activeAttachmentIndex.value])
const attachmentIsImage = computed(() => attachment.value !== null && attachment.value.mime.startsWith('image/'))
const attachmentIsText = computed(() => attachment.value !== null && attachment.value.mime === 'text/plain')
const attachmentCanPreview = computed(() => attachment.value !== null && (attachmentIsImage.value || attachment.value.mime === 'application/pdf' || attachment.value.mime === 'text/plain'))
const previewLocaleOptions = computed(() => ['zh', 'en', 'ja', 'ko', 'es', 'pt'].map(value => ({ value, label: t(`visualGameAdmin.ugc.previewLocales.${value}`) })))

onMounted(load)
onUnmounted(clearAttachmentUrl)
async function load() { detail.value = await getCreatorProject(props.id) }

async function viewAttachment(index) {
  attachmentBusy.value = true
  attachmentError.value = ''
  try {
    clearAttachmentUrl()
    activeAttachmentIndex.value = index
    const blob = await getCreatorProjectInputAttachment(props.id, index)
    attachmentUrl.value = URL.createObjectURL(blob)
    if (attachmentIsText.value) attachmentText.value = await blob.text()
  } catch (error) {
    attachmentError.value = error.message
  } finally {
    attachmentBusy.value = false
  }
}

async function downloadAttachment(index) {
  if (activeAttachmentIndex.value !== index || !attachmentUrl.value) await viewAttachment(index)
  if (!attachmentUrl.value) return
  const link = document.createElement('a')
  link.href = attachmentUrl.value
  link.download = attachment.value.file_name
  link.click()
}

function clearAttachmentUrl() {
  if (attachmentUrl.value) URL.revokeObjectURL(attachmentUrl.value)
  attachmentUrl.value = ''
  attachmentText.value = ''
  activeAttachmentIndex.value = null
}

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

async function preview() {
  const result = await createCreatorProjectPreview(props.id)
  const url = visualGamePreviewUrl(window.location.origin)
  url.searchParams.set('scenarioId', result.pack_id)
  url.searchParams.set('versionId', result.version_id)
  url.searchParams.set('preview', '1')
  url.searchParams.set('previewToken', result.preview_token)
  url.searchParams.set('locale', previewLocale.value)
  window.open(url, '_blank', 'noopener,noreferrer')
}

async function approve() {
  busy.value = true
  try { await approveCreatorProject(props.id); await load() } finally { busy.value = false }
}

async function reject() {
  if (reason.value.trim().length < 2) return
  busy.value = true
  try { await rejectCreatorProject(props.id, reason.value.trim()); await load() } finally { busy.value = false }
}

async function startProduction() {
  busy.value = true
  productionError.value = ''
  try {
    if (detail.value.project.status === 'queued') await startCreatorProject(props.id)
    else if (detail.value.project.status === 'generation_failed') await retryCreatorProject(props.id)
    else throw new Error(`Unhandled creator project production status: ${detail.value.project.status}`)
    await load()
  } catch (error) {
    productionError.value = error.code === 'SERVICE_UNAVAILABLE'
      ? t('visualGameAdmin.ugc.workerUnavailable')
      : error.message
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <section v-if="detail" class="ugc-detail">
    <header>
      <div><p>{{ t('visualGameAdmin.ugc.detail') }}</p><h1>{{ detail.project.title }}</h1></div>
      <Badge>{{ t(`visualGameAdmin.ugc.status.${detail.project.status}`) }}</Badge>
    </header>
    <div class="ugc-detail__grid">
      <Card><h2>{{ t('visualGameAdmin.ugc.idea') }}</h2><p>{{ detail.project.idea_text }}</p></Card>
      <Card><h2>{{ t('visualGameAdmin.ugc.task') }}</h2><p>{{ detail.task.current_step }} · {{ detail.task.status }}</p><p v-if="detail.task.error_message" class="error">{{ detail.task.error_message }}</p></Card>
    </div>
    <Card v-if="attachments.length > 0" class="attachment-card">
      <h2>{{ t('visualGameAdmin.ugc.attachment') }}</h2>
      <div v-for="(item, index) in attachments" :key="`${item.file_name}-${index}`" class="attachment-card__header">
        <div>
          <strong>{{ item.file_name }}</strong>
          <small>{{ item.mime }} · {{ formatBytes(item.size) }}</small>
        </div>
        <div class="attachment-card__actions">
          <Button variant="secondary" :disabled="attachmentBusy" @click="viewAttachment(index)">{{ t('visualGameAdmin.ugc.viewAttachment') }}</Button>
          <Button variant="secondary" :disabled="attachmentBusy" @click="downloadAttachment(index)">{{ t('visualGameAdmin.ugc.downloadAttachment') }}</Button>
        </div>
      </div>
      <p v-if="attachmentError" class="error">{{ attachmentError }}</p>
      <div v-if="attachmentUrl" class="attachment-preview">
        <img v-if="attachmentIsImage" :src="attachmentUrl" :alt="attachment.file_name" />
        <pre v-else-if="attachmentIsText">{{ attachmentText }}</pre>
        <iframe v-else-if="attachmentCanPreview" :src="attachmentUrl" :title="attachment.file_name"></iframe>
        <p v-else>{{ t('visualGameAdmin.ugc.attachmentPreviewUnavailable') }}</p>
      </div>
    </Card>
    <Card>
      <h2>{{ t('visualGameAdmin.ugc.logs') }}</h2>
      <ol class="timeline">
        <li v-for="step in detail.steps" :key="step.id">
          <div><strong>{{ step.step_type }}</strong><Badge size="sm">{{ step.status }}</Badge></div>
          <small>{{ step.executor_type }} · #{{ step.attempt_no }} · {{ step.started_at }}</small>
          <pre v-if="step.error_message">{{ step.error_message }}</pre>
          <details v-if="step.output"><summary>{{ t('visualGameAdmin.ugc.output') }}</summary><pre>{{ JSON.stringify(step.output, null, 2) }}</pre></details>
        </li>
      </ol>
    </Card>
    <Card v-if="detail.project.status === 'queued'" class="review-actions">
      <h2>{{ t('visualGameAdmin.ugc.productionQueue') }}</h2>
      <p class="review-note">{{ t('visualGameAdmin.ugc.productionQueueNotice') }}</p>
      <p v-if="productionError" class="error">{{ productionError }}</p>
      <Button :disabled="busy" @click="startProduction">{{ t('visualGameAdmin.ugc.startProduction') }}</Button>
    </Card>
    <Card v-if="detail.project.status === 'generation_failed'" class="review-actions">
      <h2>{{ t('visualGameAdmin.ugc.retryProduction') }}</h2>
      <p class="review-note">{{ t('visualGameAdmin.ugc.retryProductionNotice') }}</p>
      <p v-if="productionError" class="error">{{ productionError }}</p>
      <Button :disabled="busy" @click="startProduction">{{ t('visualGameAdmin.ugc.retryProduction') }}</Button>
    </Card>
    <Card v-if="pending" class="review-actions">
      <h2>{{ t('visualGameAdmin.ugc.review') }}</h2>
      <p class="review-note">{{ t('visualGameAdmin.ugc.localizationNotice') }}</p>
      <label class="preview-language"><span>{{ t('visualGameAdmin.ugc.previewLanguage') }}</span><Select v-model="previewLocale" :options="previewLocaleOptions" size="sm" /></label>
      <Button variant="secondary" :disabled="busy" @click="preview">{{ t('visualGameAdmin.ugc.preview') }}</Button>
      <Button :disabled="busy" @click="approve">{{ t('visualGameAdmin.ugc.approve') }}</Button>
      <textarea v-model="reason" :placeholder="t('visualGameAdmin.ugc.reason')"></textarea>
      <Button variant="danger" :disabled="busy || reason.trim().length < 2" @click="reject">{{ t('visualGameAdmin.ugc.reject') }}</Button>
    </Card>
  </section>
</template>

<style scoped>
.ugc-detail { display:grid; gap:var(--drama-space-xl); }
.ugc-detail > header { display:flex; align-items:center; justify-content:space-between; gap:var(--drama-space-lg); }
h1,h2 { margin:0; } h2 { margin-bottom:var(--drama-space-md); font-size:var(--drama-font-size-lg); }
.ugc-detail__grid { display:grid; grid-template-columns:2fr 1fr; gap:var(--drama-space-lg); }
.attachment-card { display:grid; gap:var(--drama-space-md); }
.attachment-card__header { display:flex; align-items:center; justify-content:space-between; gap:var(--drama-space-lg); }
.attachment-card__header > div:first-child { display:grid; gap:4px; min-width:0; }
.attachment-card__header h2 { margin-bottom:4px; }
.attachment-card__header strong { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.attachment-card__actions { display:flex; flex:none; gap:var(--drama-space-sm); }
.attachment-preview { overflow:hidden; min-height:420px; border:1px solid var(--drama-color-border); border-radius:var(--drama-radius-md); background:var(--drama-color-surface-alt); }
.attachment-preview img { display:block; max-width:100%; max-height:680px; margin:auto; object-fit:contain; }
.attachment-preview iframe { display:block; width:100%; height:680px; border:0; }
.attachment-preview > pre { max-height:680px; margin:0; overflow:auto; padding:var(--drama-space-xl); background:transparent; color:var(--drama-color-text); font-family:var(--drama-font-family-body); line-height:1.7; white-space:pre-wrap; }
.attachment-preview p { margin:0; padding:var(--drama-space-xl); color:var(--drama-color-text-muted); }
.timeline { display:grid; gap:var(--drama-space-md); margin:0; padding-left:24px; }
.timeline li { padding:var(--drama-space-md); border:1px solid var(--drama-color-border); border-radius:var(--drama-radius-md); }
.timeline li > div { display:flex; align-items:center; justify-content:space-between; }
small { color:var(--drama-color-text-muted); } pre { overflow:auto; padding:var(--drama-space-md); border-radius:var(--drama-radius-sm); background:var(--drama-color-surface-alt); white-space:pre-wrap; }
.review-actions { display:flex; flex-wrap:wrap; align-items:center; gap:var(--drama-space-md); }
.review-actions h2 { width:100%; }
.review-note { width:100%; margin:0; color:var(--drama-color-text-muted); }
.preview-language { display:flex; align-items:center; gap:var(--drama-space-sm); color:var(--drama-color-text-muted); }
textarea { flex:1 1 320px; min-height:72px; padding:var(--drama-space-md); border:1px solid var(--drama-color-border); border-radius:var(--drama-radius-md); background:var(--drama-color-surface); color:var(--drama-color-text); }
.error { color:var(--drama-color-danger); }
@media(max-width:800px){.ugc-detail__grid{grid-template-columns:1fr}.attachment-card__header{align-items:stretch;flex-direction:column}.attachment-card__actions{flex-wrap:wrap}}
</style>
